/**
 * 🔴 US-AIV2-0114 L05 (@UC0197) — HÌNH của ĐOẠN TUỲ CHỌN `[[ … {x} … ]]` trong template Sổ. Thuần, KHÔNG ném, KHÔNG lớp nào: validator Sổ
 * (`registry/validate.ts`) và các bộ sinh chạy bằng `node --experimental-strip-types` nạp file này, nên ở đây không được có cú pháp TS mà
 * strip-only không đọc được (parameter property của lớp lỗi ở `render-template.ts` làm `gen-golden-set` mù — đo khi viết L05).
 *
 * Một nguồn cho hai nơi: render (`resolveOptionalSegments` — ném `TEMPLATE_OPTIONAL_SEGMENT_INVALID` khi hình sai) và validator Sổ (đỏ lúc
 * kiểm Sổ, trước khi tới người dùng).
 */
export const OPTIONAL_SEGMENT_RE = /\[\[([^[\]]*)\]\]/g;
const PLACEHOLDER_IN_SEGMENT_RE = /\{([^{}]*)\}/g;

/**
 * 🔴 review #1111 MED-1 (@UC0197) — DANH SÁCH ĐÓNG chỗ trống được nằm trong đoạn tuỳ chọn. Mỗi mục là lời khai *"dòng này được phép BIẾN MẤT khỏi
 * phiếu khi người dùng bỏ trống"* — một dòng TIỀN không bao giờ được như vậy (validator Sổ chặn ô `money` tuyệt đối, kể cả khi có mục ở đây).
 * Liệt kê cái được PHÉP (thiếu ⇒ đỏ), không liệt kê cái bị cấm. Thêm mục = sửa ratchet `optional-segment.test.ts` kèm `reason`.
 * Hai chốt độc lập đọc bảng này: validator Sổ (theo trang — `TEMPLATE_OPTIONAL_SEGMENT_NOT_ALLOWED`) và render lúc chạy (theo tên — `TEMPLATE_OPTIONAL_SEGMENT_INVALID`).
 */
export interface AiOptionalSegmentPlaceholder {
  readonly page: string;
  readonly placeholder: string;
  readonly reason: string;
}
export const AI_OPTIONAL_SEGMENT_PLACEHOLDERS: readonly AiOptionalSegmentPlaceholder[] = Object.freeze([
  {
    page: "PAY-REPORT",
    placeholder: "ghiChuGhi",
    reason: "US-AIV2-0114 L05: ghi chú gửi thủ quỹ là ô TUỲ CHỌN, chữ (không tiền) — bỏ trống thì không in dòng 'Ghi chú cho thủ quỹ: (không có)'.",
  },
  {
    page: "PAY-REPORT",
    placeholder: "tenSuat",
    reason: "US-AIV2-tourguide-0010 (@UC0373): tên suất (bé) lên phiếu khi người khai đại diện suất có tên (nhóm phụ huynh) hoặc đã chọn bé — chữ, không tiền; nhóm thường 1 suất ⇒ rỗng ⇒ không dòng 'Cho suất'.",
  },
  {
    page: "DUE-CLAIM-WITHDRAW",
    placeholder: "tenSuat",
    reason: "UX-WITHDRAW-SEAT (@UC0198): tên suất (bé) của lời báo đang rút — phụ huynh nhiều bé phân biệt hai lời báo cùng đợt cùng số; chữ, không tiền; suất không tên riêng (nhóm thường) ⇒ rỗng ⇒ không dòng 'Của suất'.",
  },
  {
    page: "PAY-ON-BEHALF",
    placeholder: "tenNguoiDongHo",
    reason: "G-3 GUIDE-POB-ANSWER-LOOP vòng FIX HIGH-1 (@UC0218): tên NGƯỜI ĐỀ NGHỊ đóng hộ ở nhánh TRẢ LỜI — chữ, không tiền; nhánh đề nghị người đóng hộ là chính người bấm ⇒ rỗng ⇒ không dòng 'Người đóng hộ'.",
  },
  {
    page: "EXP-APPROVE",
    placeholder: "nguoiGui",
    reason: "AI-EXP-APPROVE-REQUESTS (@UC0035): tên NGƯỜI GỬI khoản chi (ghế của `createdBy`) — chữ, không tiền; không tra được ghế hoặc người gửi chính là người ứng ⇒ rỗng ⇒ không dòng 'Người gửi'.",
  },
  {
    page: "EXP-APPROVE",
    placeholder: "dinhKem",
    reason: "AI-EXP-APPROVE-REQUESTS (@UC0066): câu cố định 'có tệp đính kèm — xem trong app' khi khoản có tệp — chữ, không tiền; không tệp ⇒ rỗng ⇒ không dòng.",
  },
]);
const ALLOWED_NAMES: ReadonlySet<string> = new Set(AI_OPTIONAL_SEGMENT_PLACEHOLDERS.map((e) => e.placeholder));

/** Tên chỗ trống bên trong MỘT đoạn (thân đoạn, không gồm `[[ ]]`), theo thứ tự xuất hiện. */
export function optionalSegmentPlaceholders(inner: string): string[] {
  return [...inner.matchAll(PLACEHOLDER_IN_SEGMENT_RE)].map((m) => m[1] ?? "");
}

/**
 * `null` = không có đoạn tuỳ chọn hoặc mọi đoạn hợp lệ HÌNH; chuỗi = vì sao sai: đoạn không có ĐÚNG MỘT chỗ trống (review #1111 MED-1: hai chỗ
 * trống ⇒ một chỗ rỗng nuốt luôn chỗ kia — có thể là dòng tiền; chỗ kia VẮNG thì lỗi binding bị nuốt) · lồng nhau · dấu lẻ.
 */
export function optionalSegmentShapeError(template: string): string | null {
  if (!template.includes("[[") && !template.includes("]]")) return null;
  let problem: string | null = null;
  const rest = template.replace(OPTIONAL_SEGMENT_RE, (_m, inner: string) => {
    const n = optionalSegmentPlaceholders(inner).length;
    if (n !== 1) problem ??= `đoạn tuỳ chọn phải có ĐÚNG MỘT chỗ trống (đang có ${n})`;
    return "";
  });
  if (problem !== null) return problem;
  if (rest.includes("[[") || rest.includes("]]")) return "dấu [[ ]] lẻ hoặc lồng nhau";
  return null;
}

/** Chốt CHẠY theo TÊN (render không biết trang): chỗ trống đầu tiên trong `[[…]]` không thuộc danh sách đóng ⇒ tên đó; hợp lệ ⇒ `null`. */
export function optionalSegmentNameNotAllowed(template: string): string | null {
  for (const m of template.matchAll(OPTIONAL_SEGMENT_RE)) {
    for (const name of optionalSegmentPlaceholders(m[1] ?? "")) if (!ALLOWED_NAMES.has(name)) return name;
  }
  return null;
}
